import { useEventCallback } from '@mui/material/utils'
import useTheme from '@mui/system/useTheme'
import { bbox, feature, featureCollection } from '@turf/turf'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Layer, Source } from 'react-map-gl/mapbox'
import { LinearProgress } from '../../../components/generic.tsx'
import { type AssetLocation, isAssetLocation, type Location } from '../../../data/locations.tsx'
import { urlHash } from '../../../router.tsx'
import { useMap } from '../../../states.ts'
import { type StrictPoint, strictPoint } from '../../../strict-point.ts'
import { MapCoordsAndScale } from '../../CoordsAndScale.tsx'
import { LocationGeometry } from '../../locations/LocationGeometry.tsx'
import { LocationMarkers } from '../../locations/LocationMarkers.tsx'
import { MapBox } from '../../map/Map.tsx'
import { MapControls } from '../../map/MapControls.tsx'
import { fitBounds, flyTo, useMapCursorLngLat } from '../../map/mapUtils.ts'
import { toggleLocation } from './locationSelection.ts'
import { MapSelectionControls } from './MapSelectionControls.tsx'
import { MapSelectionOverlay } from './MapSelectionOverlay.tsx'
import type { MapSelectionMode } from './mapSelection.ts'
import { type BaseReportForm, useBaseFormValues } from './schema.ts'
import { snapPointInsideGeometry } from './snapPointInsideGeometry.tsx'
import { useSelectLocations } from './useSelectLocations.ts'

export const CreateReportFormMap = ({
	locations,
	selectedLocations,
	interactive,
	maxAssets,
	onLocationsSelected,
	children,
}: {
	selectedLocations: AssetLocation[]
	locations: AssetLocation[]
	interactive: boolean
	maxAssets: number
	onLocationsSelected: (locations: AssetLocation[]) => void
	children?: ReactNode
}) => {
	const selectLocations = useSelectLocations(onLocationsSelected)
	const [selectedLocation] = selectedLocations
	const [mode, setMode] = useState<MapSelectionMode>('click')
	const selectsSeveral = interactive && maxAssets > 1
	// One more than the form takes, so that the form shows its error, as it does for the list, and then takes no more
	const selectionCapacity = maxAssets + 1
	useMapCamera(locations, selectedLocations)

	const onMarkerClick = useEventCallback((location: Location) => {
		if (!isAssetLocation(location)) return
		if (!selectsSeveral) return selectLocations([location])
		const isSelected = selectedLocations.some(({ id }) => id === location.id)
		if (!isSelected && selectedLocations.length >= selectionCapacity) return
		selectLocations(toggleLocation(selectedLocations, location))
	})

	return (
		<MapBox
			selectedLocationId={undefined}
			disableCameraController
			logoPosition='top-left'
			attributionProps={{ position: 'top-left' }}
			{...(selectsSeveral && { cursor: 'crosshair' })}
			{...(selectsSeveral && mode === 'drag' && { dragPan: false })}
		>
			<LocationGeometry locations={locations} selectedLocations={selectedLocations} />
			<LocationMarkers
				locations={locations}
				interactive={interactive && !(selectsSeveral && mode === 'drag')}
				onLocationSelect={onMarkerClick}
				selectedLocations={selectedLocations}
			/>
			<ReportGeometryLayer />
			<GeometryProgress />
			{children}
			<CustomPointLayer interactive={interactive} selectedLocation={selectedLocation} />
			{selectsSeveral && (
				<>
					<MapSelectionOverlay
						mode={mode}
						locations={locations}
						selectedLocations={selectedLocations}
						maxAssets={selectionCapacity}
						onAdd={(added) => selectLocations([...selectedLocations, ...added])}
						onDragSelected={() => setMode('click')}
					/>
					<MapSelectionControls
						mode={mode}
						onModeChange={setMode}
						selectedCount={selectedLocations.length}
						onClearAll={() => selectLocations([])}
					/>
				</>
			)}
			<MapControls sx={{ position: 'absolute', bottom: 0, right: 0, m: 1 }} />
			<MapCoordsAndScale />
		</MapBox>
	)
}

const SHOW_PROGRESS_AFTER_MS = 300

const GeometryProgress = () => {
	const [geometryPending] = useBaseFormValues(['geometryPending'])

	return (
		geometryPending && (
			<LinearProgress
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					opacity: 0,
					animation: `showGeometryProgress 0s ${SHOW_PROGRESS_AFTER_MS}ms forwards`,
					'@keyframes showGeometryProgress': { to: { opacity: 1 } },
				}}
			/>
		)
	)
}

const ReportGeometryLayer = () => {
	const theme = useTheme()
	const [geometry] = useBaseFormValues(['geometry'])
	return (
		<Source type='geojson' data={geometry}>
			<Layer
				type='line'
				paint={{
					'line-color': theme.palette.lightningReport.reportGeometry,
					'line-width': 4,
				}}
			/>
		</Source>
	)
}

const CustomPointLayer = ({ interactive, selectedLocation }: { interactive: boolean; selectedLocation: AssetLocation | undefined }) => {
	const { setValue } = useFormContext<BaseReportForm>()
	const [customPoint] = useBaseFormValues(['customPoint'])
	const map = useMap((s) => s.map)
	const theme = useTheme()
	const [potentialNewCustomPoint, setPotentialNewCustomPoint] = useState<StrictPoint>()

	const onHoverLocationChange = useCallback(
		(hoverLatLon: [number, number] | undefined) => {
			if (!hoverLatLon || !customPoint || !map || !selectedLocation || !interactive) {
				setPotentialNewCustomPoint(undefined)
				return
			}

			const MAX_ALLOWED_DISTANCE_IN_PIXELS = 15
			const hoverPoint = snapPointInsideGeometry(map, selectedLocation.geometry, strictPoint(hoverLatLon), {
				maxSnapDistance: {
					unit: 'pixels',
					distance: MAX_ALLOWED_DISTANCE_IN_PIXELS,
				},
			})

			const newPoint = hoverPoint.isOutsideAssetGeometry ? undefined : hoverPoint
			setPotentialNewCustomPoint(newPoint)
			map.getCanvas().style.cursor = hoverPoint.isOutsideAssetGeometry ? '' : 'pointer'
		},
		[customPoint, interactive, map, selectedLocation],
	)
	useMapCursorLngLat(onHoverLocationChange)

	useEffect(() => {
		if (!map || !potentialNewCustomPoint) return

		const onMapClick = () => {
			if (potentialNewCustomPoint) {
				setValue('customPoint', potentialNewCustomPoint)
			}
		}

		map.on('click', onMapClick)
		return () => {
			map.off('click', onMapClick)
		}
	}, [map, potentialNewCustomPoint])

	const customPointData = useMemo(() => {
		const data = featureCollection([])
		if (customPoint) {
			data.features.push(feature(customPoint))
		}

		if (potentialNewCustomPoint) {
			data.features.push(feature(potentialNewCustomPoint))
		}

		return data
	}, [customPoint, potentialNewCustomPoint])

	return (
		<Source type='geojson' data={customPointData}>
			<Layer
				type='circle'
				paint={{
					'circle-color': theme.palette.lightningReport.customPoint,
					'circle-radius': 4,
					'circle-stroke-color': 'white',
					'circle-stroke-width': 1,
				}}
			/>
		</Source>
	)
}

const useMapCamera = (locations: AssetLocation[], selectedLocations: AssetLocation[]) => {
	const map = useMap((s) => s.map)
	const [geometry, customPoint] = useBaseFormValues(['geometry', 'customPoint'])
	const [initialized, setInitialized] = useState(false)
	const startupFramed = useRef(false)

	useEffect(() => {
		if (!map) return
		const timeoutId = setTimeout(() => setInitialized(true), 100)
		return () => clearTimeout(timeoutId)
	}, [map])

	// The geometry and the custom point change both from the area of interest fields and from the map itself
	useEffect(() => {
		if (!map || !initialized) return
		if (geometry.coordinates.length > 0) {
			startupFramed.current = true
			return fitBounds(map, bbox(geometry), { padding: 100 })
		}
		if (customPoint?.isOutsideAssetGeometry) return flyTo(map, customPoint)
	}, [geometry, customPoint, map, initialized])

	// Before the report has any geometry, frame the map once, as soon as there is a map so that the form never
	// shows where it was left. The selected locations get a new identity on every render, so keep this apart
	// from the effect above.
	useEffect(() => {
		if (!map || startupFramed.current) return
		startupFramed.current = frameStartup(map, locations, selectedLocations)
	}, [map, locations, selectedLocations])
}

const frameStartup = (map: mapboxgl.Map, locations: AssetLocation[], selectedLocations: AssetLocation[]) => {
	const { lat, lon, z } = urlHash.get()
	const options = { padding: 100, maxZoom: 10, animate: false }
	if (selectedLocations.length > 0) {
		fitBounds(map, bbox(featureCollection(selectedLocations.map(({ geometry }) => feature(geometry)))), options)
	} else if (lat != null && lon != null) {
		flyTo(map, [lon, lat], { zoom: z ?? 5, animate: false })
	} else if (locations.length > 0) {
		fitBounds(map, bbox(featureCollection(locations.map(({ lon, lat }) => feature({ type: 'Point', coordinates: [lon, lat] })))), options)
	} else {
		return false
	}
	return true
}
